import React, { useEffect, useRef, useState } from "react";

import type { DeviceType } from "@intelligems/ig-types";
import { Box } from "@mui/material";
import { styled, SxProps } from "@mui/material/styles";

interface ScaledIFrameProps {
  id?: string;
  device?: DeviceType;
  src: string | null | undefined;
  sx?: SxProps;
  onLoad?: () => void;
  onError?: () => void;
}

export const ScaledIFrame = React.forwardRef<
  HTMLIFrameElement,
  ScaledIFrameProps
>(({ id, device, src, sx, onLoad, onError }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{
    naturalWidth: number;
    naturalHeight: number;
    scale: number;
    scaledHeight: number;
  } | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      // Default aspect ratios when container has no height
      const defaultAspectRatio = device === "mobile" ? 2 : 1;

      // Calculate aspect ratio from container, or use default
      const aspectRatio =
        containerHeight > 0
          ? containerHeight / containerWidth
          : defaultAspectRatio;

      // Render at standard viewport sizes (like a screenshot)
      const naturalWidth = device === "mobile" ? 400 : 1200;
      const naturalHeight = naturalWidth * aspectRatio;

      // Calculate scale to fit container width
      const scale = containerWidth / naturalWidth;

      // Calculate scaled height
      const scaledHeight = naturalHeight * scale;

      setDimensions({
        naturalWidth,
        naturalHeight,
        scale,
        scaledHeight,
      });
    };

    // Initial calculation
    updateDimensions();

    // Watch for container size changes
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [device]);

  return (
    <Box
      ref={containerRef}
      sx={{
        ...sx,
        overflow: "hidden",
      }}
    >
      {dimensions && (
        <Box
          sx={{
            width: dimensions.naturalWidth * dimensions.scale,
            height: dimensions.scaledHeight,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <IFrame
            id={id}
            ref={ref}
            src={src ?? undefined}
            scale={dimensions.scale}
            naturalWidth={dimensions.naturalWidth}
            naturalHeight={dimensions.naturalHeight}
            onLoad={onLoad}
            onError={onError}
          />
        </Box>
      )}
    </Box>
  );
});

ScaledIFrame.displayName = "ScaledIFrame";

const IFrame = styled("iframe")<{
  scale: number;
  naturalWidth: number;
  naturalHeight: number;
}>(({ scale, naturalWidth, naturalHeight }) => ({
  width: `${naturalWidth}px`,
  height: `${Math.round(naturalHeight)}px`,
  border: "none",
  transform: `scale(${scale})`,
  transformOrigin: "top left",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
}));
